import { InvoiceStatusStatus } from '@navio/nsdk';
import type { NoddiIconName } from 'noddi-ui-common';
import type { BadgeVariant } from 'noddi-ui-web';
import { getInvoiceStatusTranslation } from 'noddi-util';

/**
 * A move the invoice can make from its current status, described as the action that performs it.
 * Deliberately no variant: how a move is presented depends on what else is on the page, so the
 * caller decides.
 */
export interface InvoiceStatusTransition {
  status: InvoiceStatusStatus;
  label: string;
  icon: NoddiIconName;
  intent: 'advance' | 'cancel';
  /** Present when the move is destructive enough to confirm first. */
  confirm?: { title: string; description: string; confirmText: string; cancelText: string };
}

/** Every move is one-way, but only cancellation is worth interrupting the user over. */
const CANCEL: InvoiceStatusTransition = {
  status: InvoiceStatusStatus.CANCELED,
  label: 'Cancel invoice',
  icon: 'CircleX',
  intent: 'cancel',
  confirm: {
    title: 'Cancel invoice',
    description: 'A canceled invoice is final and cannot be moved to another status again.',
    confirmText: 'Cancel invoice',
    cancelText: 'Keep it as is'
  }
};

/** Statuses an invoice can move to from its current one. Terminal statuses have none. */
const TRANSITIONS: Record<InvoiceStatusStatus, InvoiceStatusTransition[]> = {
  [InvoiceStatusStatus.DRAFT]: [
    { status: InvoiceStatusStatus.SENT, label: 'Mark as sent', icon: 'Send', intent: 'advance' },
    CANCEL
  ],
  [InvoiceStatusStatus.SENT]: [
    { status: InvoiceStatusStatus.PAID, label: 'Mark as paid', icon: 'CircleCheck', intent: 'advance' },
    CANCEL
  ],
  [InvoiceStatusStatus.PAID]: [],
  [InvoiceStatusStatus.CANCELED]: []
};

const BADGE_VARIANTS: Record<InvoiceStatusStatus, BadgeVariant> = {
  [InvoiceStatusStatus.DRAFT]: 'outline',
  [InvoiceStatusStatus.SENT]: 'info',
  [InvoiceStatusStatus.PAID]: 'success',
  [InvoiceStatusStatus.CANCELED]: 'destructive'
};

export const getInvoiceStatusBadgeVariant = (status: InvoiceStatusStatus): BadgeVariant =>
  BADGE_VARIANTS[status] ?? 'default';

/** Lifecycle order, not enum declaration order — filters read better that way. */
export const INVOICE_STATUS_OPTIONS = [
  InvoiceStatusStatus.DRAFT,
  InvoiceStatusStatus.SENT,
  InvoiceStatusStatus.PAID,
  InvoiceStatusStatus.CANCELED
].map((status) => ({ label: getInvoiceStatusTranslation(status), value: status }));

export const getInvoiceStatusTransitions = (status: InvoiceStatusStatus): InvoiceStatusTransition[] =>
  TRANSITIONS[status] ?? [];

/** Only drafts can be deleted — anything sent has left the building. */
export const canDeleteInvoice = (status: InvoiceStatusStatus): boolean => status === InvoiceStatusStatus.DRAFT;
